import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import CropLandscapeIcon from "@mui/icons-material/CropLandscape";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Input,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Theme,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { area, intersect, length } from "@turf/turf";

import type { Feature, FeatureCollection, Geometry } from "geojson";
import { Field, GRASS_TYPE } from "model";
import { useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useMatch,
  useNavigate,
  useParams,
} from "react-router-dom";

import { Alert, AlertColor } from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import {
  ConfirmCancellationDialog,
  DrawerButtonContainer,
  DrawerButtonGroup,
  DrawerForm,
  FullScreenDialog,
  MobileBottomDrawer,
} from "common/components";
import { useTranslation } from "common/locales";
import { isOfflineWriteBlockedError } from "common/utils/offlineWriteGuard";
import { queryKeys } from "common/queryKeys";
import {
  booleanValid,
  polygonToMultiPolygon,
  processFieldResponse,
  useArea,
} from "common/utils";
import DrawingHelpModal from "features/mapDrawTools/DrawingHelpModal";
import { EditorSelectionType } from "features/mapDrawTools/enums";
import {
  ARABLE_TYPE,
  ArableField,
  Farm,
  FIELD_TYPE,
  TmpFieldDetails,
  useArableField,
  useArableFields,
  useField,
  useFields,
} from "model";
import { processArableFieldResponse } from "model/arableFieldTypes";
import { arableFieldApi } from "model/useArableFieldQueries";
import { fieldApi } from "model/useFieldQueries";
import {
  createArableField,
  createField,
  deleteAllTilesForField,
  updateArableField,
  updateField,
} from "services";
import { useAppStore, useUIStore } from "stores";
import { analytics, AnalyticsEvents } from "../../common/analytics";
import ArableFieldDetailsEditor from "./ArableFieldDetailsEditor";
import FieldDetailsEditor from "./FieldDetailsEditor";
import FieldStats from "./FieldStats";
import FieldTypeRadio from "./FieldTypeRadio";
const PREFIX = "FieldInput";

const classes = {
  formControl: `${PREFIX}-formControl`,
  drawingHintContainer: `${PREFIX}-drawingHintContainer`,
  drawingHint: `${PREFIX}-drawingHint`,
  mobileDialogContent: `${PREFIX}-mobileDialogContent`,
  mobileDialogActions: `${PREFIX}-mobileDialogActions`,
  mobileTopHint: `${PREFIX}-mobileTopHint`,
};

const FormControlStyled = styled(DrawerForm)(({ theme }: { theme: Theme }) => ({
  [`&.${classes.formControl}`]: {
    minWidth: 300,
    paddingBottom: theme.spacing(2),
  },
}));

const MobileDialogContentStyled = styled(DialogContent)(({ theme }) => ({
  [`&.${classes.mobileDialogContent}`]: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden", // Let the inner div handle scrolling
    position: "relative", // For absolutely positioned children
  },
}));

const DrawingHintContainerStyled = styled(Container)(
  ({ theme }: { theme: Theme }) => ({
    [`&.${classes.drawingHintContainer}`]: {
      bottom: 0,
    },
  })
);

const MobileDialogActionsStyled = styled(DialogActions)(({ theme }) => ({
  [`&.${classes.mobileDialogActions}`]: {
    position: "absolute",
    bottom: "64px", // Position above the navigation bar
    left: 0,
    right: 0,
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: 0,
    width: "100%",
    boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
    zIndex: 1,
  },
}));

const MobileButtonContainer = styled(DrawerButtonContainer)(({ theme }) => ({
  position: "relative",
  height: "auto",
  minHeight: "auto",
  justifyContent: "center",
}));

const MobileButtonGroup = styled(DrawerButtonGroup)(({ theme }) => ({
  position: "relative",
  right: "auto",
  display: "flex",
  gap: theme.spacing(2),
  justifyContent: "flex-end",
  width: "100%",
}));

export const MobileTopHintContainerStyled = styled(Grid)(
  ({ theme }: { theme: Theme }) => ({
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    pointerEvents: "none",
    padding: `calc(env(safe-area-inset-top) + 20px) 16px 10px 16px`,
    backgroundColor: "transparent",
  })
);

const DrawingHintStyled = styled(Alert)`
  margin: 0 auto;
  max-width: 600px;
  pointer-events: auto;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  white-space: normal;
  .MuiAlert-message {
    overflow-wrap: break-word;
    word-wrap: break-word;
    hyphens: auto;
  }
`;

const StyledTextField = styled(TextField)(({ theme }) => ({
  minWidth: "300px",
  paddingBottom: theme.spacing(2),
}));

const ButtonGroup = styled("div")(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(2),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const ButtonContainer = styled(Container)({
  position: "relative",
  flex: "0 0 auto",
  display: "block",
  height: "70px",
  width: "100%",
  background: "white",
});

export default function FieldInput() {
  const { t } = useTranslation();
  const { fromM2, label: areaUnitLabel } = useArea();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();
  const [fieldNameError, setFieldNameError] = useState(false);
  const [vegetationTypeInputError, setVegetationTypeInputError] =
    useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(true);
  const [open, setOpen] = useState(false);
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const [isLoading, setIsLoading] = useState(false);

  // Use React Query to get field count
  const { data: allFields = [] } = useFields();
  const fieldCount = allFields.length;

  // Use Zustand for editor state
  const editorIsInTentativeMode = useUIStore(
    (state) => state.editorIsInTentativeMode
  );
  const editorSelectionType = useUIStore((state) => state.editorSelectionType);
  const tmpFeatures = useUIStore((state) => state.tmpFeatures);
  const tentativeFeature = useUIStore((state) => state.tentativeFeature);
  const setTmpFeatures = useUIStore((state) => state.setTmpFeatures);
  const setTentativeFeature = useUIStore((state) => state.setTentativeFeature);
  const setEditorSelectionType = useUIStore(
    (state) => state.setEditorSelectionType
  );
  const setEditorIsInTentativeMode = useUIStore(
    (state) => state.setEditorIsInTentativeMode
  );
  const deleteTmpFeatures = useUIStore((state) => state.deleteTmpFeatures);
  const setZoomToGeom = useUIStore((state) => state.setZoomToGeom);
  const openAfterFieldCreatedDialog = useUIStore(
    (state) => state.openAfterFieldCreatedDialog
  );

  const [tmpField, setTmpField] = useState<TmpFieldDetails>({
    guid: "new",
    name: "",
    farm: currentFarmId ?? "",
  });

  const fieldGeom = {
    type: "FeatureCollection",
    features: tmpField?.geom ? tmpField?.geom : [],
  } as FeatureCollection;

  useEffect(() => {
    if (fieldGeom.features.length > 0) {
      setZoomToGeom(fieldGeom);
    }
  }, [fieldGeom, setZoomToGeom]);

  const { fieldId } = useParams<{ fieldId: string }>();

  // Get all arable fields to determine field type (allFields is already declared above)
  const { data: allArableFields = [] } = useArableFields();

  // Determine field type by checking which list contains the fieldId
  const determinedFieldType = useMemo(() => {
    if (!fieldId) return undefined;
    const isGrassField = allFields.some((field) => field.guid === fieldId);
    if (isGrassField) return FIELD_TYPE.GRASS;
    const isArableField = allArableFields.some(
      (field) => field.guid === fieldId
    );
    if (isArableField) return FIELD_TYPE.ARABLE;
    return undefined;
  }, [fieldId, allFields, allArableFields]);

  // Use React Query to fetch field data for editing - conditionally based on field type
  const { data: fieldToEdit } = useField(
    determinedFieldType === FIELD_TYPE.GRASS ? fieldId : undefined
  );
  const { data: arableFieldToEdit } = useArableField(
    determinedFieldType === FIELD_TYPE.ARABLE ? fieldId : undefined
  );

  const [fieldType, setFieldType] = useState<FIELD_TYPE>(
    determinedFieldType ??
      (fieldToEdit
        ? FIELD_TYPE.GRASS
        : arableFieldToEdit
        ? FIELD_TYPE.ARABLE
        : FIELD_TYPE.GRASS)
  );

  const [fieldTypeError, setFieldTypeError] = useState(false);
  // there should only ever be 1 field boundary, so taking the first element that's a polygon, or undefined if there are no matches ([][0] is undefined)
  const tmpFieldGeom =
    tmpFeatures.filter((f) => f.geometry?.type === "Polygon")[0] ||
    tentativeFeature;
  // Track the fieldId to only reset geometry when editing a different field
  const [initializedFieldId, setInitializedFieldId] = useState<string | null>(
    null
  );

  useEffect(() => {
    const curField: Field | ArableField | undefined =
      fieldToEdit ?? arableFieldToEdit;
    if (curField) {
      // Only initialize features if we're editing a new/different field
      // This prevents resetting geometry when the user is actively editing
      if (initializedFieldId !== curField.guid) {
        setInitializedFieldId(curField.guid);
        setTmpField(curField);
        setTmpFeatures([curField.geom] as Feature[]);
        setZoomToGeom({
          type: "FeatureCollection",
          features: [curField.geom],
        });
      }
    } else {
      // Reset when creating a new field (no curField)
      if (initializedFieldId !== "new") {
        setInitializedFieldId("new");
        setTmpFeatures([]);
      }
    }
  }, [
    fieldToEdit,
    arableFieldToEdit,
    initializedFieldId,
    setTmpFeatures,
    setZoomToGeom,
  ]);

  const handleFieldNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTmpField({ ...tmpField, name: e.target.value });
    setFieldNameError(false);
  };

  const handleFieldTypeChange = (value?: FIELD_TYPE) => {
    if (value) {
      setFieldType(value);
    } else {
      setFieldTypeError(true);
    }
  };

  const handleSave = async () => {
    try {
      const fieldNameSet = !!tmpField.name;
      setFieldNameError(!fieldNameSet);
      if (fieldType === FIELD_TYPE.GRASS) {
        const grassTypeInputError = !tmpField.grassType;
        setVegetationTypeInputError(grassTypeInputError);
        if (grassTypeInputError) {
          return;
        }
      }

      if (fieldType === FIELD_TYPE.ARABLE) {
        const arableTypeInputError = !tmpField.arableType;
        setVegetationTypeInputError(arableTypeInputError);
        if (arableTypeInputError) {
          return;
        }
      }
      if (!fieldNameSet) {
        return;
      }
      if (fieldTypeError) {
        return;
      }

      if (!isGeomValid(tmpFieldGeom)) {
        if (!tentativeFeature || !isGeomValid(tentativeFeature)) {
          return notify(t("fieldinput.boundary.error"), "error");
        } else {
          // defensive logic
          setIsLoading(true);
          setTmpFeatures([tentativeFeature]);
          setTentativeFeature(null); // TODO: this should belong to the editor
          setEditorSelectionType(EditorSelectionType.NONE); // reset editor
          setEditorIsInTentativeMode(false);
          await create(tentativeFeature);
        }
      }
      setIsLoading(true);

      // TODO: this should belong to the editor
      setEditorSelectionType(EditorSelectionType.NONE); // reset editor
      setEditorIsInTentativeMode(false);

      if (tmpField.guid === "new" && currentFarmId) {
        await create(tmpFieldGeom);
      } else {
        await update();
      }
    } catch (e: unknown) {
      if (!isOfflineWriteBlockedError(e)) {
        notify(t("fieldinput.save.error"), "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmationClose = () => {
    setOpen(false);

    // reset editor
    deleteTmpFeatures();
    setTentativeFeature(null);
    setEditorSelectionType(EditorSelectionType.NONE);
    setEditorIsInTentativeMode(false);
  };

  const handleCancel = () => {
    setOpen(true);
  };

  const { severity, hintId } = getHint(
    editorIsInTentativeMode,
    editorSelectionType,
    tmpFeatures,
    tentativeFeature
  );

  // Get current path for matching routes
  const isAddField = location.pathname.startsWith("/add-field");
  const isEditField = location.pathname.startsWith("/edit-field");
  const basePath = isAddField
    ? "/add-field"
    : isEditField
    ? "/edit-field/:fieldId"
    : "";

  const enterDetailsMatch = useMatch(`${basePath}/enter-details`);
  const drawBoundaryMatch = useMatch(`${basePath}/draw-boundary`);
  const baseRouteMatch = useMatch(basePath);

  // Redirect to enter-details if on base route (mobile needs explicit sub-route)
  useEffect(() => {
    if (isMobile && baseRouteMatch && isAddField) {
      navigate("enter-details", { replace: true });
    }
  }, [isMobile, baseRouteMatch, isAddField, navigate]);

  if (isMobile) {
    // Show enter-details view
    if (enterDetailsMatch) {
      return (
        <FullScreenDialog>
          <DialogTitle>{t("fieldinput.title.mobile")}</DialogTitle>
          <MobileDialogContentStyled className={classes.mobileDialogContent}>
            <FormControlStyled className={classes.formControl}>
              <InputLabel htmlFor="input-name" variant="standard">
                {t("edit.fieldname.label")}
              </InputLabel>
              <Input
                required
                error={fieldNameError}
                id="input-name"
                value={tmpField.name}
                onChange={handleFieldNameChange}
                autoFocus={true}
              />
            </FormControlStyled>

            <Container sx={{ alignContent: "left", paddingTop: "20px" }}>
              <FormControlStyled className={classes.formControl}>
                <FieldTypeRadio
                  disabled={!!(fieldToEdit || arableFieldToEdit)}
                  onChange={handleFieldTypeChange}
                  currentFieldType={fieldType}
                  fieldTypeInputError={fieldTypeError}
                  label={"fieldinput.fieldType.text"}
                />
              </FormControlStyled>
            </Container>
            {fieldType === FIELD_TYPE.GRASS && (
              <FieldDetailsEditor
                field={tmpField}
                handleAssignedToChange={handleAssignedToChange}
                handleGrassTypeChange={handleGrassTypeChange}
                fieldNameError={fieldNameError}
                currentGrassType={fieldToEdit?.grassType ?? undefined}
                grassTypeInputError={vegetationTypeInputError}
              />
            )}
            {fieldType === FIELD_TYPE.ARABLE && (
              <ArableFieldDetailsEditor
                field={tmpField}
                handleArableTypeChange={handleArableTypeChange}
                fieldNameError={fieldNameError}
                currentArableType={arableFieldToEdit?.arableType ?? undefined}
                arableTypeInputError={vegetationTypeInputError}
              />
            )}
          </MobileDialogContentStyled>
          <MobileDialogActionsStyled className={classes.mobileDialogActions}>
            <MobileButtonContainer>
              <MobileButtonGroup>
                <Button color="secondary" onClick={handleCancel}>
                  {t("edit.button.cancel")}
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  disabled={
                    !tmpField.name ||
                    (fieldType === FIELD_TYPE.GRASS && !tmpField.grassType) ||
                    (fieldType === FIELD_TYPE.ARABLE && !tmpField.arableType)
                  }
                  onClick={() => {
                    if (!tmpField.name) {
                      setFieldNameError(true);
                      return;
                    }

                    // Validate vegetation type based on field type
                    if (fieldType === FIELD_TYPE.GRASS && !tmpField.grassType) {
                      setVegetationTypeInputError(true);
                      return;
                    }

                    if (
                      fieldType === FIELD_TYPE.ARABLE &&
                      !tmpField.arableType
                    ) {
                      setVegetationTypeInputError(true);
                      return;
                    }

                    // All validations passed, proceed to next step
                    setFieldNameError(false);
                    setVegetationTypeInputError(false);
                    navigate("draw-boundary");
                  }}
                >
                  {t("edit.button.next")}
                </Button>
              </MobileButtonGroup>
              <ConfirmCancellationDialog
                open={open}
                onClose={handleConfirmationClose}
              />
            </MobileButtonContainer>
          </MobileDialogActionsStyled>
        </FullScreenDialog>
      );
    }

    // Show draw-boundary view
    if (drawBoundaryMatch) {
      return (
        <Dialog
          open={true}
          fullScreen
          style={{ pointerEvents: "none" }} // Make the entire dialog non-interactive
          BackdropProps={{
            style: {
              backgroundColor: "transparent",
              pointerEvents: "none", // Ensure backdrop doesn't block
            },
          }}
          // Completely remove backdrop
          hideBackdrop={true}
          slotProps={{
            paper: {
              style: {
                backgroundColor: "transparent",
                boxShadow: "none",
                pointerEvents: "none",
                overflow: "visible", // Ensure content isn't clipped
              },
            },
          }}
        >
          <div style={{ whiteSpace: "normal" }}>
            <DrawingHelpModal
              isOpen={snackbarOpen && fieldCount === 0 && !tmpFieldGeom}
              handleClose={handleSnackbarClose}
            />
          </div>
          <MobileTopHintContainerStyled
            className={classes.mobileTopHint}
            container
            style={{ pointerEvents: "auto" }} // Re-enable pointer events for the hint
          >
            <Grid size={{ xs: 12 }}>
              <DrawingHintStyled
                className={classes.drawingHint}
                severity={severity}
              >
                {t(hintId)}
              </DrawingHintStyled>
            </Grid>
          </MobileTopHintContainerStyled>
          <MobileBottomDrawer container style={{ pointerEvents: "auto" }}>
            {" "}
            {/* Re-enable for buttons */}
            <Grid size={{ xs: 12 }}>
              <FieldStats geom={tmpFieldGeom} direction="row" />
            </Grid>
            <Grid size={{ xs: 12 }} container justifyContent="flex-end">
              <Button
                color="secondary"
                onClick={() => navigate("enter-details")}
              >
                {t("edit.button.back")}
              </Button>
              <Button
                color="primary"
                onClick={handleSave}
                disabled={isLoading || !isGeomValid(tmpFieldGeom)}
              >
                {t("edit.button.save")}
              </Button>
            </Grid>
          </MobileBottomDrawer>
        </Dialog>
      );
    }

    // Default fallback - shouldn't happen but just in case
    return null;
  } else {
    const geomIsValid = tmpFieldGeom && booleanValid(tmpFieldGeom);

    return (
      <Box
        sx={{
          backgroundColor: "transparent",
          minHeight: "100%",
        }}
      >
        <Container
          sx={{
            paddingTop: "16px",
            backgroundColor: "transparent",
          }}
        >
          <List>
            <ListItem>
              <StyledTextField
                required
                id="field-input-name"
                label={t("edit.fieldname.label")}
                value={tmpField.name}
                helperText={fieldNameError ? t("edit.required.error") : ""}
                error={fieldNameError}
                fullWidth
                onChange={handleFieldNameChange}
                name="name"
                autoFocus
              />
            </ListItem>
            <ListItem>
              <FieldTypeRadio
                disabled={!!(fieldToEdit || arableFieldToEdit)}
                onChange={handleFieldTypeChange}
                currentFieldType={fieldType}
                fieldTypeInputError={fieldTypeError}
                label={"fieldinput.fieldType.text"}
              />
            </ListItem>
          </List>
        </Container>
        {fieldType === FIELD_TYPE.GRASS && (
          <FieldDetailsEditor
            field={tmpField}
            handleAssignedToChange={handleAssignedToChange}
            handleGrassTypeChange={handleGrassTypeChange}
            fieldNameError={fieldNameError}
            currentGrassType={fieldToEdit?.grassType ?? undefined}
            grassTypeInputError={vegetationTypeInputError}
          />
        )}
        {fieldType === FIELD_TYPE.ARABLE && (
          <ArableFieldDetailsEditor
            field={tmpField}
            handleArableTypeChange={handleArableTypeChange}
            fieldNameError={fieldNameError}
            currentArableType={arableFieldToEdit?.arableType ?? undefined}
            arableTypeInputError={vegetationTypeInputError}
          />
        )}
        <Container sx={{ backgroundColor: "transparent" }}>
          <List>
            <ListItem>
              <ListItemIcon>
                <AspectRatioIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  geomIsValid
                    ? `${fromM2(area(tmpFieldGeom)).toFixed(
                        2
                      )} ${areaUnitLabel}`
                    : t("fieldinput.na")
                }
                secondary={t("fieldinput.area.text")}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CropLandscapeIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  geomIsValid
                    ? `${Math.round(
                        length(tmpFieldGeom, { units: "meters" })
                      )} ${t("fieldview.perimeter.unit")}`
                    : t("fieldinput.na")
                }
                secondary={t("fieldview.perimeter.text")}
              />
            </ListItem>
          </List>
        </Container>
        <Divider />
        <ButtonContainer>
          <ButtonGroup>
            <Button
              color="secondary"
              onClick={handleCancel}
              disabled={isLoading}
            >
              {t("edit.button.cancel")}
            </Button>
            <Button
              color="primary"
              onClick={handleSave}
              disabled={isLoading || !isGeomValid(tmpFieldGeom)}
            >
              {t("edit.button.save")}
            </Button>
          </ButtonGroup>
          <ConfirmCancellationDialog
            open={open}
            onClose={handleConfirmationClose}
          />
        </ButtonContainer>
        <DrawingHintContainerStyled className={classes.drawingHintContainer}>
          <DrawingHintStyled
            className={classes.drawingHint}
            severity={severity}
          >
            {t(hintId)}
          </DrawingHintStyled>
        </DrawingHintContainerStyled>
      </Box>
    );
  }

  function handleAssignedToChange(value?: string) {
    setTmpField({ ...tmpField, animalGroup: value });
  }

  function handleGrassTypeChange(value?: GRASS_TYPE) {
    setTmpField({ ...tmpField, grassType: value });
    setVegetationTypeInputError(!value);
  }

  function handleArableTypeChange(value?: ARABLE_TYPE) {
    setTmpField({ ...tmpField, arableType: value });
    setVegetationTypeInputError(!value);
  }

  async function create(geom: Feature) {
    if (!currentFarmId) throw new Error("A farm is required to create a field");
    let newField;
    // Remove the placeholder guid when creating a new field
    const { guid: _guid, ...fieldWithoutGuid } = tmpField;
    // Transform geometry: convert Polygon to MultiPolygon and wrap in FeatureCollection
    // This is the format expected by the backend API
    const preparedGeom = {
      type: "FeatureCollection",
      features: [polygonToMultiPolygon(geom)],
    } as FeatureCollection;
    if (fieldType === FIELD_TYPE.GRASS) {
      fieldWithoutGuid.arableType = undefined;
      const createResponse = await createField({
        ...fieldWithoutGuid,
        dryMatterNow: 0,
        farm: currentFarmId,
        geom: preparedGeom,
        area: 0,
        tiles: [],
      });
      // Fetch the full field data after creation (creation response may not include full geometry)
      const fullFieldResponse = await fieldApi.getOne(createResponse.guid);
      // Process API response to convert FeatureCollection geometry to Feature
      const [processedField] = processFieldResponse([fullFieldResponse]);
      newField = processedField;
      // Store processed field in cache with empty tiles - React Query will poll for tiles
      queryClient.setQueryData(queryKeys.fields.detail(newField.guid), {
        ...newField,
        tiles: [],
      });
      // Update parent farm's fields array so the field shows on the map
      if (currentFarmId) {
        const farm = queryClient.getQueryData<Farm>(
          queryKeys.farms.detail(currentFarmId)
        );
        if (farm && !farm.fields?.includes(newField.guid)) {
          queryClient.setQueryData<Farm>(
            queryKeys.farms.detail(currentFarmId),
            {
              ...farm,
              fields: [...(farm.fields || []), newField.guid],
            }
          );
        }
      }
    } else {
      fieldWithoutGuid.grassType = undefined;
      const createResponse = await createArableField({
        fieldType: FIELD_TYPE.ARABLE,
        ...fieldWithoutGuid,
        arableType: fieldWithoutGuid.arableType ?? ARABLE_TYPE.BARLEY,
        farm: currentFarmId,
        geom: preparedGeom,
        area: 0,
        tiles: [],
      });
      // Fetch the full field data after creation (creation response may not include full geometry)
      const fullFieldResponse = await arableFieldApi.getOne(
        createResponse.guid
      );
      // Process API response to convert FeatureCollection geometry to Feature
      const [processedField] = processArableFieldResponse([fullFieldResponse]);
      newField = processedField;
      // Store processed field in cache with empty tiles - React Query will poll for tiles
      queryClient.setQueryData(queryKeys.arableFields.detail(newField.guid), {
        ...newField,
        tiles: [],
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.arableFields.all,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.arableFields.ids(currentFarmId!),
      });
    }
    notify(t("fieldInput.add.success", { fieldName: tmpField.name }));
    analytics.track(AnalyticsEvents.FIELD_CREATED);
    deleteTmpFeatures();
    setTentativeFeature(null); // reset tentative feature
    openAfterFieldCreatedDialog();
    if (fieldCount < 1) {
      // show congrats dialog
      analytics.track(AnalyticsEvents.AFTER_FIRST_FIELD_DIALOG_OPENED);
    } else {
      // show general "want another field?" dialog
      analytics.track(AnalyticsEvents.AFTER_NTH_FIELD_DIALOG_OPENED);
    }
    navigate(`/field/${newField.guid}`);
  }

  async function update(): Promise<void> {
    const jaccardIndex = (lhs: Geometry | Feature, rhs: Geometry | Feature) => {
      const isectFeat = intersect(lhs, rhs);
      const isectArea = isectFeat ? area(isectFeat) : 0;
      const unionArea = area(lhs) + area(rhs) - isectArea;
      return isectArea / unionArea;
    };

    const existingGeom = (fieldToEdit?.geom ??
      arableFieldToEdit?.geom) as Feature;
    const geomChanged =
      jaccardIndex(tmpFieldGeom.geometry, existingGeom) < 0.99;

    if (geomChanged) {
      await deleteAllTilesForField(tmpField.guid);
    }

    // Transform geometry: convert Polygon to MultiPolygon and wrap in FeatureCollection
    // This is the format expected by the backend API
    const preparedGeom = {
      type: "FeatureCollection",
      features: [polygonToMultiPolygon(tmpFieldGeom)],
    } as FeatureCollection;

    if (fieldType === FIELD_TYPE.GRASS) {
      await updateField({
        ...tmpField,
        dryMatterNow:
          !geomChanged && fieldToEdit?.dryMatterNow
            ? fieldToEdit.dryMatterNow / 10000
            : 0,
        geom: preparedGeom,
        area: (!geomChanged && fieldToEdit?.area) || 0,
      });

      if (geomChanged) {
        // Clear tiles in cache - React Query will poll for new tiles automatically
        queryClient.setQueryData(
          queryKeys.fields.detail(tmpField.guid),
          (old: Field | undefined) => (old ? { ...old, tiles: [] } : old)
        );
      }
      // Invalidate to refetch latest data
      await queryClient.invalidateQueries({
        queryKey: queryKeys.fields.detail(tmpField.guid),
      });
    } else {
      await updateArableField({
        guid: tmpField.guid,
        name: tmpField.name,
        farm: tmpField.farm,
        arableType: tmpField.arableType ?? ARABLE_TYPE.BARLEY,
        geom: preparedGeom,
        area: (!geomChanged && fieldToEdit?.area) || 0,
      });
      if (geomChanged) {
        // Clear tiles in cache - React Query will poll for new tiles automatically
        queryClient.setQueryData(
          queryKeys.arableFields.detail(tmpField.guid),
          (old: ArableField | undefined) => (old ? { ...old, tiles: [] } : old)
        );
      }
      // Invalidate to refetch latest data
      await queryClient.invalidateQueries({
        queryKey: queryKeys.arableFields.detail(tmpField.guid),
      });
    }

    notify(t("fieldInput.edit.success", { fieldName: tmpField.name }));
    analytics.track(AnalyticsEvents.FIELD_UPDATED);
    deleteTmpFeatures();
    navigate(`/field/${tmpField.guid}`);
  }

  function handleSnackbarClose() {
    setSnackbarOpen(false);
  }
}

function isGeomValid(geom: Feature) {
  return geom && booleanValid(geom);
}

function getHint(
  editorIsInTentativeMode: boolean,
  editorSelectionType: EditorSelectionType,
  tmpFeatures: Array<Feature>,
  tentativeFeature: Feature | null
): { hintId: string; severity: AlertColor } {
  let severity: AlertColor = "info";
  let hintId = "map.clickOnPencilButton.hint"; // edit mode, no feature exists
  // TODO: simplify it
  if (editorIsInTentativeMode) {
    if (!tentativeFeature) {
      // not enough points
      hintId = "map.provideThreePoints.hint";
    } else if (isGeomValid(tentativeFeature)) {
      // valid tentative feature
      hintId = "map.doubleClickToFinish.hint";
    } else {
      // invalid geometry
      hintId = "map.provideValidGeometry.hint";
      severity = "error";
    }
  } else {
    // in edit mode
    if (tmpFeatures.length > 0) {
      // feature exists
      if (editorSelectionType === EditorSelectionType.HANDLES) {
        // selected handles, tell them how to delete
        if (isGeomValid(tmpFeatures[0])) {
          hintId = "map.editingField.hint"; // not using "map.deleteHandles.hint" for now because of the bug in react-draw-map-gl not providing correct handles
        } else {
          // invalid geometry
          hintId = "map.provideValidGeometry.hint";
          severity = "error";
        }
      } else if (editorSelectionType === EditorSelectionType.FEATURE) {
        // feature selected
        if (isGeomValid(tmpFeatures[0])) {
          hintId = "map.editingField.hint"; // valid geometry, can select points and edit, delete
        } else {
          // invalid geometry
          hintId = "map.provideValidGeometry.hint";
          severity = "error";
        }
      } else {
        // no feature selected (pencil button inactive)
        if (isGeomValid(tmpFeatures[0])) {
          hintId = "map.clickOnPencilButtonToEdit.hint"; // valid geometry, can activate pencil and edit
        } else {
          hintId = "map.provideValidGeometry.hint"; // invalid geometry
          severity = "error";
        }
      }
    }
  }

  return {
    severity,
    hintId,
  };
}
